import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Loader from "../../../components/Loader";
import { MdDelete, MdDownload } from "react-icons/md";
import { toast } from "react-toastify";
import CustomToolTip from "../../../components/ToolTip";
import moment from "moment";

import Hoc from "../../../components/HOC";
import { EmployeeContext } from "../../../Context/Employee";
import { getEmployeeById } from "../../../Services/employee";
import SimpleTable from "../../../components/SimpleTable";

const ArchivedDocuments = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { fetchEmployeeDocuments, deleteEmployeeDocument } = useContext(EmployeeContext);
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState(null);
  const [Loading, setLoading] = useState(false);
  const location = useLocation();
  const { employeeName } = location.state || {};
  const [currentEmployeeName, setCurrentEmployeeName] = useState(employeeName);

  const fetchArchivedDocumentsData = async () => {
    try {
      setLoading(true);
      const data = await fetchEmployeeDocuments(id, "Archived"); // Fetch archived documents
     
      // Reverse the order of the fetched data
      const reversedData = [...data].reverse(); // Create a shallow copy before reversing to avoid mutating original array if it's used elsewhere
      setDocuments(reversedData);
      setError(null);
    } catch (err) {
      setError("Failed to fetch archived documents. Please try again.");
      toast.error("Failed to load archived documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArchivedDocumentsData();

    if (!currentEmployeeName) {
      const fetchEmployee = async () => {
        try {
          const employee = await getEmployeeById(id);
          if (employee) {
            setCurrentEmployeeName(`${employee.fname || ''} ${employee.lname || ''}`.trim());
          }
        } catch (err) {
          toast.error("Failed to fetch employee");
        }
      };
      fetchEmployee();
    }
  }, [id, currentEmployeeName]);

  const downloadDocument = (docData) => {
    try {
      if (!docData.downloadLink) {
        toast.error("Download link not available");
        return;
      }

      const link = window.document.createElement('a');
      link.href = docData.downloadLink;
      // Use originalFilename if available, otherwise fallback to old logic
      let filename = docData.originalFilename;
      if (!filename) {
        const extension = getFileExtension(docData.fileType);
        const documentType = docData.documentType ? docData.documentType.toLowerCase() : 'document';
        filename = `${documentType}.${extension}`;
      }
      link.download = filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      toast.success(`Downloading ${filename}`);
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Failed to download document");
    }
  };

  const getFileExtension = (mimeType) => {
    const extensions = {
      'application/pdf': 'pdf',
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'text/plain': 'txt'
    };
    return extensions[mimeType] || 'file';
  };

  const handleDocumentAction = async (action, employeeId, docData) => {
    if (action === "delete") {
      const result = await deleteEmployeeDocument(employeeId, docData.documentType, "Archived"); // Pass 'Archived' status
  
      if (result.success) {
        toast.success(result.message);
        setDocuments(result.data); // update local state if needed
      } else {
        toast.error(result.message);
      }
    } else if (action === "download") {
      downloadDocument(docData);
    }
  };
  
  // Define columns for SimpleTable
  const columns = [
    {
      name: 'Document Type',
      selector: 'documentType',
      // Add className if needed, matching the original header style
      // className: 'px-6 py-3 text-left text-xs font-semibold text-[#1e293b] uppercase tracking-wider w-[25%]'
    },
    {
      name: 'Issue Date',
      selector: 'issueDate',
      // Add renderCell to format date if needed, matching the original cell rendering
      renderCell: (row) => row.issueDate ? moment(row.issueDate).format("MM-DD-YYYY") : "Not Applicable"
    },
    {
      name: 'Expiry Date',
      selector: 'expiryDate',
      // Add renderCell to format date if needed, matching the original cell rendering
      renderCell: (row) => row.expiryDate ? moment(row.expiryDate).format("MM-DD-YYYY") : "Not Applicable"
    },
    {
      name: 'Updated By',
      selector: 'updatedBy',
       renderCell: (row) => row.updatedBy || "N/A"
    },
    {
      name: 'Actions',
      selector: 'actions',
      renderCell: (row) => (
        <div className="flex items-center justify-center space-x-4">
          <button
            onClick={() => handleDocumentAction("delete", id, row)}
            className="text-red-500 hover:text-red-700 disabled:cursor-not-allowed p-2 rounded-full hover:bg-red-50 transition-colors"
          >
            <>
              <MdDelete size={20} data-tip data-for="Delete" />
              <CustomToolTip id="Delete-Archived" title="Delete" />
            </>
          </button>
          <button
            onClick={() => handleDocumentAction("download", id, row)}
            className=" disabled:cursor-not-allowed p-2 rounded-full  transition-colors hover:bg-gray-200"
          >
            <>
              <MdDownload size={20} data-tip data-for="Download" />
              <CustomToolTip id="Download-Archived" title="Download" />
            </>
          </button>
        </div>
      ),
      headerClassName: 'text-center',
    },
  ];

  const goToUploadDocuments = () => {
    navigate(`/manage-employees/uploaddocuments/${id}`, {
      state: { employeeName: employeeName || currentEmployeeName },
    });
  };

  return (
    <div className="flex w-full flex-col px-4 py-6">
      <Loader isLoading={Loading} />

      {/* Header — matches UploadDocuments layout */}
      <div className="mb-6 w-full rounded-lg border border-gray-200 bg-white px-4 py-4 shadow-sm sm:px-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center sm:gap-4">
          <h2 className="order-1 min-w-0 text-center text-lg font-semibold leading-snug text-[#201F1E] sm:order-none sm:col-start-2 sm:row-start-1 sm:px-2 sm:text-xl">
            <span className="block text-xs font-medium uppercase tracking-wide text-[#201F1E]  sm:text-sm">
              Archived
            </span>
            <span className="mt-0.5 block text-[#656464]">
              Documents for{" "}
              <span className="break-words font-semibold text-[#656464]">
                {currentEmployeeName || "—"}
              </span>
            </span>
          </h2>
          <div className="order-2 flex items-center justify-between gap-3 sm:contents">
            <button
              type="button"
              onClick={goToUploadDocuments}
              className="inline-flex h-10 shrink-0 items-center justify-center rounded-[4px] border border-[#D1D1D1] bg-white px-4 text-sm font-medium text-[#201F1E] transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#201F1E]/20 focus:ring-offset-1 sm:col-start-1 sm:row-start-1"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={() => navigate("/manage-employees")}
              className="inline-flex h-10 shrink-0 items-center justify-center rounded-[4px] bg-[#201F1E] px-5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#2b2a29] focus:outline-none focus:ring-2 focus:ring-[#201F1E]/30 focus:ring-offset-1 sm:col-start-3 sm:row-start-1"
            >
              Manage employees
            </button>
          </div>
        </div>
      </div>

      {/* Document Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <SimpleTable columns={columns} data={documents} />
      </div>
    </div>
  );
};

export default Hoc(ArchivedDocuments); 